/**
 * Vehicle Selection Popup
 * Handles the car/bike selection popup functionality
 */
class VehicleSelectionPopup extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.init();
  }

  init() {
    const _this = this;
    const show = this.dataset.show;
    const showMobile = this.dataset.showMb === "true";
    
    if (!Shopify.designMode) {
      if (show === "show-on-all-pages" || show === "show-on-homepage") {
        if (this.shouldShowPopup()) {
          setTimeout(() => {
            this.initPopup();
          }, 2000); // Show after 2 seconds
        }
      }
    }
  }

  shouldShowPopup() {
    // Check if user has already made a selection
    const vehicleChoice = getCookie("vehicle-selection");
    return vehicleChoice === "";
  }

  initPopup() {
    const _this = this;
    const showMobile = this.dataset.showMb === "true";
    
    // Don't show on mobile if disabled
    if (!showMobile && window.innerWidth < 768) {
      return false;
    }

    this.modal = new tingle.modal({
      footer: false,
      stickyFooter: false,
      closeMethods: ["overlay", "button", "escape"],
      closeLabel: "Close",
      cssClass: [
        "vehicle-selection-popup-modal",
        showMobile ? "show-all" : "disable-on-mobile"
      ],
      onOpen: function () {
        document.dispatchEvent(new CustomEvent("modal:opened"));
        document.documentElement.classList.add("open-popup");
        root.style.setProperty("padding-right", getScrollBarWidth.init() + "px");
        
        // Add animation classes
        const cards = this.modalBox.querySelectorAll('.vehicle-card');
        cards.forEach((card, index) => {
          setTimeout(() => {
            card.classList.add('animate-in');
          }, index * 100);
        });
      },
      onClose: function () {
        document.dispatchEvent(new CustomEvent("modal:closed"));
        document.documentElement.classList.remove("open-popup");
        root.style.removeProperty("padding-right");
      },
      beforeClose: function () {
        _this.onCloseEvent();
        document.documentElement.classList.remove("open-popup");
        root.style.removeProperty("padding-right");
        return true;
      }
    });

    this.modal.setContent(this.innerHTML);
    this.modal.open();
    
    this.bindEvents();
  }

  bindEvents() {
    const _this = this;
    
    // Handle vehicle selection buttons
    const vehicleButtons = this.modal.modalBox.querySelectorAll('.vehicle-card__button');
    vehicleButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        const vehicleType = this.dataset.vehicleType;
        const url = this.href;
        
        // Store the user's choice
        setCookie("vehicle-selection", vehicleType, 30); // 30 days
        
        // Add selection animation
        _this.addSelectionAnimation(this, vehicleType);
        
        // Redirect after animation
        setTimeout(() => {
          window.location.href = url;
        }, 500);
      });
    });

    // Handle "do not show again" option
    const doNotShowAgain = this.modal.modalBox.querySelector('.do-not-show-again');
    if (doNotShowAgain) {
      doNotShowAgain.addEventListener('click', function(e) {
        e.preventDefault();
        setCookie("vehicle-selection", "dismissed", 365); // 1 year
        _this.modal.close();
      });
    }

    // Handle card hover effects
    const cards = this.modal.modalBox.querySelectorAll('.vehicle-card');
    cards.forEach(card => {
      card.addEventListener('mouseenter', function() {
        this.classList.add('hovered');
      });
      
      card.addEventListener('mouseleave', function() {
        this.classList.remove('hovered');
      });
    });
  }

  addSelectionAnimation(button, vehicleType) {
    const card = button.closest('.vehicle-card');
    
    // Add selection class
    card.classList.add('selected');
    
    // Add ripple effect
    const ripple = document.createElement('div');
    ripple.classList.add('ripple-effect');
    button.appendChild(ripple);
    
    // Remove ripple after animation
    setTimeout(() => {
      ripple.remove();
    }, 600);
  }

  onCloseEvent() {
    // Clean up any event listeners or animations
    if (this.modal && this.modal.modalBox) {
      const buttons = this.modal.modalBox.querySelectorAll('.vehicle-card__button');
      buttons.forEach(button => {
        button.removeEventListener('click', this.handleVehicleSelection);
      });
    }
  }
}

// Register the custom element
customElements.define("vehicle-selection-popup", VehicleSelectionPopup);

// Utility functions (if not already defined)
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return "";
}

function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

// Additional CSS for animations (injected dynamically)
const additionalStyles = `
  .vehicle-card.selected {
    transform: scale(1.02);
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.2);
  }
  
  .ripple-effect {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: scale(0);
    animation: ripple 0.6s linear;
    pointer-events: none;
  }
  
  @keyframes ripple {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }
  
  .vehicle-card.animate-in {
    animation: slideInUp 0.6s ease forwards;
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .vehicle-card.hovered {
    transform: translateY(-8px);
  }
  
  .vehicle-card.selected .vehicle-card__button {
    background: var(--color-primary) !important;
    transform: scale(1.05);
  }
`;

// Inject additional styles
const styleSheet = document.createElement('style');
styleSheet.textContent = additionalStyles;
document.head.appendChild(styleSheet);

// Initialize popup when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const popup = document.querySelector('vehicle-selection-popup');
  if (popup) {
    // Popup will auto-initialize
  }
});
